/**
 * NotFound — proper 404 page for the SPA catch-all.
 *
 * Previously the catch-all redirected to `/` which caused search
 * engines to see homepage content at non-existent URLs (soft-404 per
 * the 2026-10-06 audit). Now any unmatched URL renders this page with:
 *   • <meta name="robots" content="noindex,follow"> so Google drops it
 *   • A clear user message with pathway back to the main sections
 *   • A visually dignified mobile-friendly layout
 *
 * We cannot set a real HTTP 404 status from a client-rendered SPA — a
 * genuine 404 would need SSR/Edge. However, the `noindex` tag + visible
 * "not found" copy is sufficient for Google to classify the URL as a
 * soft 404 and drop it from the index.
 */
import { Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { TreePine, Flame, Home as HomeIcon, Search } from "lucide-react";

export default function NotFound() {
  const location = useLocation();
  const path = location.pathname + (location.search || "");
  return (
    <>
      <Helmet>
        <title>Page not found · IndianFamilyTree.com</title>
        <meta name="robots" content="noindex,follow" />
        <meta name="description" content="The page you requested does not exist. Explore family trees, memorials and heritage pages on IndianFamilyTree.com." />
      </Helmet>
      <main
        className="min-h-[70vh] flex items-center justify-center px-5 py-16"
        data-testid="not-found-page"
      >
        <div className="w-full max-w-md text-center">
          <div className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: "#D97706" }}>
            404 · Not Found
          </div>
          <h1
            className="mt-2 text-3xl sm:text-4xl font-bold leading-tight"
            style={{ color: "#3B2E1F", fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            This page is not in our family archive.
          </h1>
          <p className="mt-3 text-sm leading-relaxed" style={{ color: "#6B5A3F" }}>
            The address <span className="font-mono text-[12px] break-all" style={{ color: "#78350F" }}>{path}</span> doesn&rsquo;t match any family tree, memorial, place or article we currently publish.
          </p>

          <div className="mt-7 grid grid-cols-2 gap-3">
            <Link
              to="/family-trees"
              data-testid="not-found-trees"
              className="flex flex-col items-center gap-1 py-4 px-3 rounded-2xl border bg-white active:scale-95 transition-transform"
              style={{ borderColor: "rgba(59,46,31,0.08)" }}
            >
              <TreePine className="w-6 h-6" style={{ color: "#15803D" }} />
              <span className="text-[12.5px] font-semibold" style={{ color: "#3B2E1F" }}>Family Trees</span>
            </Link>
            <Link
              to="/celebrate"
              data-testid="not-found-memorials"
              className="flex flex-col items-center gap-1 py-4 px-3 rounded-2xl border bg-white active:scale-95 transition-transform"
              style={{ borderColor: "rgba(59,46,31,0.08)" }}
            >
              <Flame className="w-6 h-6" style={{ color: "#C2410C" }} />
              <span className="text-[12.5px] font-semibold" style={{ color: "#3B2E1F" }}>Memorials</span>
            </Link>
            <Link
              to="/search"
              data-testid="not-found-search"
              className="flex flex-col items-center gap-1 py-4 px-3 rounded-2xl border bg-white active:scale-95 transition-transform"
              style={{ borderColor: "rgba(59,46,31,0.08)" }}
            >
              <Search className="w-6 h-6" style={{ color: "#1D4ED8" }} />
              <span className="text-[12.5px] font-semibold" style={{ color: "#3B2E1F" }}>Search people</span>
            </Link>
            <Link
              to="/"
              data-testid="not-found-home"
              className="flex flex-col items-center gap-1 py-4 px-3 rounded-2xl border bg-white active:scale-95 transition-transform"
              style={{ borderColor: "rgba(59,46,31,0.08)" }}
            >
              <HomeIcon className="w-6 h-6" style={{ color: "#D97706" }} />
              <span className="text-[12.5px] font-semibold" style={{ color: "#3B2E1F" }}>Home</span>
            </Link>
          </div>

          <p className="mt-6 text-[11px]" style={{ color: "#8A7A5A" }}>
            If you reached this page from a link, the family or memorial may have been renamed or set to private.
          </p>
        </div>
      </main>
    </>
  );
}
